import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import type { Content } from '@plone/types';
import type { LocationDataProps, LocationMarker } from '@fhnw/types/location';
import {
  Location,
  LocationMarkerProps,
} from '@designsystem/stories/04-organisms/Location';
import { normalizeLink } from '@fhnw/helpers/DesignSystem/normalizeContent';
import { addLineBreaksAsHTML } from '@fhnw/helpers/text/addLineBreaks';
import containsHTML from '@fhnw/helpers/text/containsHTML';
import { sanitize } from '@designsystem/utils/sanitize';
import LocationMapContent from '../../LocationMap/LocationMapContent';
import { messages } from './messages';

interface LocationProps extends LocationDataProps {
  custom?: boolean;
  location?: LocationDataProps;
}

export interface BlockDataProps {
  locations?: LocationProps[];
}

interface ViewProps {
  data: BlockDataProps;
  content: Content;
  isEdit?: boolean;
  block?: string;
  onChangeBlock?: (blockId: string, newData: any) => void;
}

const LOCATION_KEYS = [
  'title',
  'tab_name',
  'department',
  'street',
  'zip_city',
  'email',
  'phone',
  'latitude',
  'longitude',
  'button_link',
];

const View = (props: ViewProps) => {
  const intl = useIntl();
  const _content = useSelector(
    (state: { content: { data: Content } }) => state.content.data,
  );
  const content =
    props?.content && (props.content?.['@id'] || props.content?.parent)
      ? props.content
      : _content;
  const data = { ...props?.data };
  const id = content?.['@id'];
  const [tab, setTab] = useState(0);
  const location = useSelector(
    (state: { router: { location: { pathname: string; search: string } } }) =>
      state?.router?.location,
  );
  const type =
    content?.['@type'] ||
    (location.pathname.endsWith('/add')
      ? location.search.match(/type=([^&]+)/)?.[1]
      : '');

  const items: (LocationMarkerProps & { marker?: LocationMarker })[] = [];
  const markers: (LocationMarker & { location?: number })[] = [];
  let locations = data?.locations || [];
  if (!locations.length && type === 'Location') {
    locations = [
      {
        location: content,
      },
    ];
  }

  locations.forEach((location) => {
    const existing: LocationDataProps | undefined = location?.location;
    const custom: boolean | undefined = location?.custom;

    if (!existing && !custom) {
      return;
    }

    const data: LocationDataProps = {};
    LOCATION_KEYS.forEach((key) => {
      const typedKey = key as keyof LocationDataProps;
      data[typedKey] = (
        custom && location?.[typedKey]
          ? location[typedKey]
          : existing?.[typedKey]
      ) as string | undefined;
    });

    if (!data.title && !data.tab_name) {
      return;
    }

    const department = data.department
      ? containsHTML(data.department)
        ? data.department
        : addLineBreaksAsHTML(data.department)
      : undefined;

    const street = data.street
      ? containsHTML(data.street)
        ? data.street
        : addLineBreaksAsHTML(data.street)
      : undefined;

    const item: LocationMarkerProps & { marker?: LocationMarker } = {
      tab_name: data.tab_name || data.title,
      title: data.title,
      children: (
        <>
          {department && (
            <p>
              <strong
                dangerouslySetInnerHTML={{
                  __html: sanitize(department as string),
                }}
              />
            </p>
          )}
          {(data.street || data.zip_city) && (
            <p>
              {data.street && (
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitize(street as string),
                  }}
                />
              )}
              {data.street && data.zip_city && <br />}
              {data.zip_city && <>{data.zip_city}</>}
            </p>
          )}
        </>
      ),
      actions: [],
    };

    if (data.phone) {
      item.actions?.push({
        icon: 'phone',
        title: intl.formatMessage(messages.Call),
        label: data.phone,
        href: `tel:${data.phone}`,
      });
    }

    if (data.email) {
      item.actions?.push({
        icon: 'closed_envelope',
        title: intl.formatMessage(messages.SendEmail),
        label: data.email,
        href: `mailto:${data.email}`,
      });
    }

    if (
      data.longitude &&
      parseFloat(data.longitude as string) &&
      data.latitude &&
      parseFloat(data.latitude as string)
    ) {
      markers.push({
        id: `${id}-${items.length + 1}`,
        location: items.length,
        longitude: parseFloat(data.longitude as string),
        latitude: parseFloat(data.latitude as string),
        zoomLevel: 15,
      });

      item.marker = markers[markers.length - 1];

      item.actions?.push({
        icon: 'pointer_destination',
        label: intl.formatMessage(messages.PlanRoute),
        href: `https://www.google.ch/maps/@${data.latitude},${data.longitude},18z`,
        target: '_blank',
      });
    }

    if (data.button_link) {
      item.actions?.push({
        icon: 'arrow_right_triangle',
        icon_after: true,
        label: intl.formatMessage(messages.MoreInformation),
        href: normalizeLink(data.button_link),
      });
    }

    items.push(item);
  });

  if (!items.length) {
    return null;
  }

  const map = (
    <LocationMapContent
      locations={markers}
      className="media__object media__object--embed"
      activeLocation={
        typeof tab !== 'undefined' ? items[tab]?.marker : undefined
      }
      onChange={(location) => {
        if (location) {
          setTab((location as LocationMarker & { location: number })?.location);
        }
      }}
    />
  );

  return (
    <Location
      items={items}
      media={{
        children: map,
      }}
      current={tab}
      onTabChange={setTab}
    />
  );
};

export default View;
